// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// SLA-target status for the alarm detail's metadata grid, matching the
// "SLA target (Critical)" cell in the Helios Operator Console design. Ack-time
// budgets come from the severity policy table (theme/severity.ts SEVERITY[sev]
// .slaSec — ported straight from the Operator Console mock's `sev()`), not
// from any backend field: AEM has no SLA concept.
// TODO(aem): move to a real SLA policy source if/when the alarm-rule API
// models one.

import type { Alarm } from '../types/alarm';
import { SEVERITY } from '../theme/severity';
import { formatSeconds, secondsBetween } from './time';
import type { StatusTone } from './statusTone';

export interface SlaStatus {
  /** The severity's ack budget as "M:SS", or null when it has none. */
  targetText: string | null;
  /** e.g. "Acknowledged", "Overdue", "Closed". */
  statusText: string;
  tone: StatusTone;
}

/** Where an alarm stands against its severity's ack-time budget. */
export function slaStatus(alarm: Alarm): SlaStatus {
  const budgetSec = SEVERITY[alarm.sev]?.slaSec ?? 0;
  const targetText = budgetSec > 0 ? formatSeconds(budgetSec) : null;

  if (alarm.closed) {
    return { targetText, statusText: 'Closed', tone: 'idle' };
  }

  if (alarm.acknowledgedAt) {
    if (budgetSec === 0) return { targetText, statusText: 'Acknowledged', tone: 'ok' };
    const ackSec = secondsBetween(alarm.eventTimestamp, alarm.acknowledgedAt);
    if (ackSec == null) return { targetText, statusText: 'Acknowledged', tone: 'ok' };
    return ackSec <= budgetSec
      ? { targetText, statusText: 'Acknowledged', tone: 'ok' }
      : { targetText, statusText: 'Acknowledged late', tone: 'fault' };
  }

  if (budgetSec === 0) {
    return { targetText, statusText: 'No SLA', tone: 'idle' };
  }
  if (alarm.raisedAgo == null) {
    return { targetText, statusText: 'Unknown', tone: 'idle' };
  }
  return alarm.raisedAgo * 60 <= budgetSec
    ? { targetText, statusText: 'Awaiting ack', tone: 'ok' }
    : { targetText, statusText: 'Overdue', tone: 'fault' };
}
